import { useCallback, useEffect, useRef, useState } from "react";

import EventService from "service/event/EventService";
import { RAIL_OPEN, RAIL_CLOSE } from "../railEvents";
import focusFirstFocusable from "./focusFirstFocusable";

/**
 * The editor rail as a STACK of write sheets, newest on top.
 *
 * Every v3 section's rail used to hold exactly one sheet, so a second
 * "+ Add …" click blew away whatever was half-typed. This hook turns the rail
 * into a list: each click pushes a fresh sheet at position 1 and the older
 * ones slide down, so a clinician can line several up and save them one at a
 * time. It was built for Chief complaints first and then lifted here, because
 * all six sections have byte-for-byte the same rail shape (formOpen + editing
 * + an ADD listener + an EDIT listener + RAIL_OPEN/RAIL_CLOSE).
 *
 * Two rules govern what a click does:
 *
 *   1. "+ Add …" never buries a blank add sheet. If the stack already holds
 *      an add sheet with nothing entered on it, the click does NOT stack a
 *      second one — a new blank sheet on top of an untouched blank sheet is
 *      just a way to lose your place. (Scope: this guards the toolbar button
 *      only. A row click is a different intent — the user picked a specific
 *      record — so an update sheet opens on top regardless.)
 *   2. The same record never opens twice. An edit event re-fired for a record
 *      already on the stack surfaces the open sheet instead of cloning it;
 *      two update sheets for one record would race each other's write.
 *
 * A refused click is never silently swallowed: the sheet it points at is
 * scrolled into view, pulsed, and announced to assistive tech.
 *
 * Saves are serialised across the stack — while one sheet is in flight the
 * others' Save is disabled. Every v2 write carries `expectedVersion` from a
 * just-in-time read, so two concurrent saves would make the second collide
 * with the version the first just bumped.
 *
 * The caller owns ONE sheet component. It receives `formId`, `editing`,
 * `blockedBySibling` and the three callbacks, and must:
 *   • report whether it has anything on it yet via onFilledChange
 *   • bracket its own write with onSavingChange(id, true/false)
 *   • call onClose(id) to dismiss itself (Cancel, or after a successful save)
 *   • namespace any DOM id it renders with formId — N sheets are on screen at
 *     once and duplicate ids break every label's htmlFor.
 */

const FLAG_MS = 1200;

const asArray = (v) => (v == null ? [] : Array.isArray(v) ? v : [v]);

const useSheetStack = (options) => {
  const opts = options || {};
  const addEvents = asArray(opts.addEvents);
  const editEvents = asArray(opts.editEvents);
  // Joined into plain strings so the subscribe effect has a statically
  // checkable dep: every call site passes module-level constant arrays, whose
  // identity is stable, but the linter cannot see that.
  const addKey = addEvents.join("|");
  const editKey = editEvents.join("|");
  const {
    getRecord,
    getRecordId,
    blankMessage,
    dupMessage,
    /** Guard the rail while the section has no patient yet. */
    enabled,
    /**
     * Insertion order for new sheets.
     *   • "top" (default) — newest at index 0, matches CC / Dx / Med
     *     which show the most-recently-opened sheet first.
     *   • "bottom" — new sheets go to the end of the stack, so a "+
     *     Add another test" affordance lands the sibling BELOW the
     *     current sheet. Used by LI's rail.
     */
    insertAt,
  } = opts;

  /** [{ id, editing }] — index 0 is the top sheet. */
  const [sheets, setSheets] = useState([]);
  /**
   * The sheet a refused click points at. `n` is a restart token: re-flagging
   * the SAME sheet must replay the pulse, and setState with an equal value is
   * a no-op in React, so the id alone could never retrigger it.
   */
  const [nudge, setNudge] = useState({ id: null, n: 0 });
  /** What that refusal should say out loud. */
  const [notice, setNotice] = useState("");
  /** id of the sheet currently writing, or null. */
  const [savingId, setSavingId] = useState(null);
  /**
   * id -> whatever identity that sheet is currently claiming (a relationship
   * key, an allergen name, an ICD code…). Sheets validate uniqueness against
   * the SERVER list, which cannot see a sibling that has not saved yet — so
   * two open add sheets could both claim "Father" and both save. This lets a
   * form fold its siblings' claims into its own duplicate check.
   */
  const [claims, setClaims] = useState({});

  // The listeners are registered once, so their deps must stay stable. These
  // refs let them read CURRENT state without re-subscribing on every keystroke.
  const sheetsRef = useRef(sheets);
  useEffect(() => { sheetsRef.current = sheets; }, [sheets]);
  const filledRef = useRef({});
  const idRef = useRef(0);
  const nudgeTimer = useRef(null);
  /** id -> the sheet's DOM node, so a refusal can scroll to it. */
  const nodesRef = useRef({});

  const flag = useCallback((id, message) => {
    setNudge((p) => ({ id: id, n: p.n + 1 }));
    setNotice(message || "");
    // Pulsing a sheet that is below the fold is the same as swallowing the
    // click. The rail has no scroll container of its own (deliberately — one
    // would clip the section's autocomplete dropdowns), so this scrolls the
    // page.
    const node = nodesRef.current[id];
    if (node && node.scrollIntoView) node.scrollIntoView({ block: "nearest" });
    if (nudgeTimer.current) clearTimeout(nudgeTimer.current);
    nudgeTimer.current = setTimeout(() => {
      setNudge((p) => ({ id: null, n: p.n }));
      setNotice("");
    }, FLAG_MS);
  }, []);
  useEffect(() => () => {
    if (nudgeTimer.current) clearTimeout(nudgeTimer.current);
  }, []);

  /** id of the sheet whose DOM node should grab focus on next mount.
   *  Set by every push() so the reviewer's next keystroke lands in
   *  the new sheet without a mouse trip. Cleared inside setNode once
   *  the focus call fires (or when the sheet is closed before mount).
   *  Kept as a ref, not state — focus timing must be synchronous with
   *  the ref callback, and re-rendering to consume "just pushed" would
   *  race the DOM mount. */
  const pendingFocusRef = useRef(null);

  const push = useCallback((editing) => {
    idRef.current += 1;
    const id = idRef.current;
    pendingFocusRef.current = id;
    setSheets((prev) => (
      insertAt === "bottom"
        ? [...prev, { id, editing: editing || null }]
        : [{ id, editing: editing || null }, ...prev]
    ));
  }, [insertAt]);

  const closeSheet = useCallback((id) => {
    delete filledRef.current[id];
    delete nodesRef.current[id];
    setClaims((prev) => {
      if (!(id in prev)) return prev;
      const out = Object.assign({}, prev);
      delete out[id];
      return out;
    });
    setSheets((prev) => prev.filter((s) => s.id !== id));
    setNudge((p) => (p.id === id ? { id: null, n: p.n } : p));
    // Releases the stack-wide save lock if a sheet is dismissed mid-write.
    setSavingId((s) => (s === id ? null : s));
  }, []);

  const onFilledChange = useCallback((id, filled) => {
    filledRef.current[id] = filled;
  }, []);

  const onClaimChange = useCallback((id, claim) => {
    setClaims((prev) => {
      const next = claim || null;
      if (prev[id] === next) return prev; // no-op keeps sibling renders down
      const out = Object.assign({}, prev);
      if (next === null) delete out[id];
      else out[id] = next;
      return out;
    });
  }, []);

  const onSavingChange = useCallback((id, saving) => {
    setSavingId(saving ? id : (s) => (s === id ? null : s));
  }, []);

  /**
   * Re-point a sheet at a different record.
   *
   * Some forms morph: hospitalisation's ADD sheet turns into an UPDATE when the
   * picked ICD matches an entry that already exists. Without this the rail's
   * dedupe key stays null, so clicking that same row in the table pushes a
   * SECOND sheet for the record and the two writes silently clobber each other.
   */
  const setRecord = useCallback((id, record) => {
    setSheets((prev) =>
      prev.map((s) => (s.id === id ? { id: s.id, editing: record || null } : s))
    );
  }, []);

  const setNode = useCallback((id, node) => {
    if (node) {
      nodesRef.current[id] = node;
      // If this is the sheet push() just registered as pending, move
      // keyboard focus into it as soon as the browser commits. See
      // focusFirstFocusable for the rAF + disabled-skip rules.
      if (pendingFocusRef.current === id) {
        pendingFocusRef.current = null;
        focusFirstFocusable(node);
      }
    } else {
      delete nodesRef.current[id];
    }
  }, []);

  /** Close every sheet — used when the rail must reset (e.g. patient swap). */
  const closeAll = useCallback(() => {
    filledRef.current = {};
    nodesRef.current = {};
    setSheets([]);
    setNudge((p) => ({ id: null, n: p.n }));
    setSavingId(null);
  }, []);

  // Shell contract: the table takes this column's width while the stack is
  // empty.
  const isOpen = sheets.length > 0;
  useEffect(() => {
    EventService.emit(isOpen ? RAIL_OPEN : RAIL_CLOSE);
  }, [isOpen]);
  useEffect(() => () => EventService.emit(RAIL_CLOSE), []);

  const enabledRef = useRef(enabled);
  useEffect(() => { enabledRef.current = enabled; }, [enabled]);

  useEffect(() => {
    const openAdd = () => {
      // Rule 1 — don't bury an add sheet that has nothing on it yet.
      const blank = sheetsRef.current.find(
        (s) => !s.editing && !filledRef.current[s.id]
      );
      if (blank) {
        flag(blank.id, blankMessage);
        return;
      }
      push(null);
    };
    const onAdd = () => {
      if (enabledRef.current === false) return;
      openAdd();
    };
    const onEdit = (payload) => {
      if (enabledRef.current === false) return;
      const rec = getRecord ? getRecord(payload) : payload;
      if (!rec) {
        // A bare payload on the edit channel means "open a blank add" — the
        // lab rail has no separate ADD event and relies on this.
        openAdd();
        return;
      }
      // Rule 2 — one sheet per record.
      const key = getRecordId ? getRecordId(rec) : null;
      const already =
        key == null
          ? null
          : sheetsRef.current.find(
              (s) => s.editing && getRecordId(s.editing) === key
            );
      if (already) {
        flag(already.id, dupMessage ? dupMessage(rec) : "");
        return;
      }
      push(rec);
    };

    addEvents.forEach((e) => EventService.addListener(e, onAdd));
    editEvents.forEach((e) => EventService.addListener(e, onEdit));
    return () => {
      addEvents.forEach((e) => EventService.removeListener(e, onAdd));
      editEvents.forEach((e) => EventService.removeListener(e, onEdit));
    };
    // Keyed on the joined event names, not the array identities — see addKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    addKey,
    editKey,
    flag,
    push,
    getRecord,
    getRecordId,
    blankMessage,
    dupMessage,
  ]);

  return {
    sheets,
    isOpen,
    savingId,
    nudge,
    notice,
    closeSheet,
    closeAll,
    onFilledChange,
    onSavingChange,
    onClaimChange,
    setRecord,
    setNode,
    /** Everything one sheet needs, ready to spread onto the form component. */
    sheetProps: (sheet, index) => ({
      formId: sheet.id,
      editing: sheet.editing,
      ordinal: index + 1,
      showOrdinal: sheets.length > 1,
      blockedBySibling: savingId !== null && savingId !== sheet.id,
      onFilledChange,
      onSavingChange,
      onClaimChange,
      /** What the OTHER open sheets are claiming right now. */
      siblingClaims: Object.keys(claims)
        .filter((k) => String(k) !== String(sheet.id))
        .map((k) => claims[k])
        .filter(Boolean),
      onRecordChange: setRecord,
      onClose: closeSheet,
    }),
  };
};

export default useSheetStack;
